import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { AuthProvider, useAuth } from './context/AuthContext';
import { WidgetVisibilityProvider } from './context/WidgetVisibilityContext';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';
import InstallAppPrompt from './components/InstallAppPrompt';

import Login from './pages/Login';
import SelfOnboard from './pages/SelfOnboard';
import Dashboard from './pages/Dashboard';
import Users from './pages/Users';
import CreateInvoice from './pages/CreateInvoice';
import Invoices from './pages/Invoices';
import Payments from './pages/Payments';
import ReportsLayout from './pages/reports/ReportsLayout';
import ReportsOverview from './pages/reports/ReportsOverview';
import ReportsFinancials from './pages/reports/ReportsFinancials';
import ReportsProperty from './pages/reports/ReportsProperty';
import MyInvoices from './pages/MyInvoices';
import Profile from './pages/Profile';
import HostelProfileEdit from './pages/HostelProfileEdit';
import HostelDashboard from './pages/HostelDashboard';
import RoomBedManagement from './pages/RoomBedManagement';
import TenantOnboarding from './pages/TenantOnboarding';
import TenantManagement from './pages/TenantManagement';
import DailyGuests from './pages/DailyGuests';
import Leads from './pages/Leads';
import CreateTenantProfile from './pages/CreateTenantProfile';
import ViewTenantUser from './pages/ViewTenantUser';
import SettingsRouter from './pages/SettingsRouter';
import TenantAccount from './pages/TenantAccount';
import TenantHostel from './pages/TenantHostel';
import GuestStay from './pages/GuestStay';
import ComplaintsRouter from './pages/ComplaintsRouter';
import ExpenseManagement from './pages/ExpenseManagement';
import DocumentManagement from './pages/DocumentManagement';
import DuesManagement from './pages/DuesManagement';
import SubscriptionAdmin from './pages/SubscriptionAdmin';
import Bookings from './pages/Bookings';
import SchedulingRules from './pages/SchedulingRules';
import PaymentsLedger from './pages/PaymentsLedger';
import Credits from './pages/Credits';

const AppRoutes: React.FC = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return <div className="loading-container">Loading...</div>;
  }

  return (
    <Routes>
      <Route path="/login" element={!user ? <Login /> : <Navigate to="/dashboard" />} />
      <Route path="/join/:hostelCode" element={<SelfOnboard />} />
      
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout>
              <Dashboard />
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Layout>
              <Dashboard />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/users"
        element={
          <ProtectedRoute requireSuperAdmin>
            <Layout>
              <Users />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/create-invoice"
        element={
          <ProtectedRoute requireAdmin blockSuperAdmin blockHostelAdmin>
            <Layout>
              <CreateInvoice />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/invoices"
        element={
          <ProtectedRoute requireAdmin blockSuperAdmin>
            <Layout>
              <Invoices />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/payments"
        element={
          <ProtectedRoute requireAdmin blockSuperAdmin blockHostelAdmin>
            <Layout>
              <Payments />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/payments-ledger"
        element={
          <ProtectedRoute requireHostelAdmin blockHostelAdmin>
            <Layout>
              <PaymentsLedger />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/reports"
        element={
          <ProtectedRoute requireAdmin blockSuperAdmin>
            <Layout>
              <ReportsLayout />
            </Layout>
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="overview" replace />} />
        <Route path="overview" element={<ReportsOverview />} />
        <Route path="financials" element={<ReportsFinancials />} />
        <Route path="property" element={<ReportsProperty />} />
      </Route>

      <Route
        path="/my-account"
        element={
          <ProtectedRoute blockSuperAdmin blockHostelAdmin>
            <Layout>
              <TenantAccount />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/my-hostel"
        element={
          <ProtectedRoute blockSuperAdmin blockHostelAdmin>
            <Layout>
              <TenantHostel />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/my-stay"
        element={
          <ProtectedRoute blockSuperAdmin blockHostelAdmin>
            <Layout>
              <GuestStay />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/my-invoices"
        element={
          <ProtectedRoute blockSuperAdmin blockHostelAdmin>
            <Layout>
              <MyInvoices />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/profile"
        element={
          <ProtectedRoute blockSuperAdmin requireHostelAdmin>
            <Layout>
              <Profile />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/profile/edit"
        element={
          <ProtectedRoute blockSuperAdmin requireHostelAdmin>
            <Layout>
              <HostelProfileEdit />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/hostels"
        element={
          <ProtectedRoute requireSuperAdmin>
            <Layout>
              <HostelDashboard />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/rooms-beds"
        element={
          <ProtectedRoute requireHostelAdmin>
            <Layout>
              <RoomBedManagement />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/tenant-onboarding"
        element={
          <ProtectedRoute requireHostelAdmin>
            <Layout>
              <TenantOnboarding />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/tenants/create"
        element={
          <ProtectedRoute requireHostelAdmin>
            <Layout>
              <CreateTenantProfile />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/tenants/edit/:userId"
        element={
          <ProtectedRoute requireHostelAdmin>
            <Layout>
              <CreateTenantProfile />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/tenants/view/:userId"
        element={
          <ProtectedRoute requireHostelAdmin>
            <Layout>
              <ViewTenantUser />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/tenants"
        element={
          <ProtectedRoute requireHostelAdmin>
            <Layout>
              <TenantManagement />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/daily-guests"
        element={
          <ProtectedRoute requireHostelAdmin>
            <Layout>
              <DailyGuests />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/leads"
        element={
          <ProtectedRoute requireHostelAdmin>
            <Layout>
              <Leads />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/complaints"
        element={
          <ProtectedRoute>
            <Layout>
              <ComplaintsRouter />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/expenses"
        element={
          <ProtectedRoute requireHostelAdmin>
            <Layout>
              <ExpenseManagement />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/documents"
        element={
          <ProtectedRoute requireStaff>
            <Layout>
              <DocumentManagement />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/dues"
        element={
          <ProtectedRoute requireHostelAdmin>
            <Layout>
              <DuesManagement />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings"
        element={
          <ProtectedRoute requireHostelAdmin>
            <Layout>
              <SettingsRouter />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/credits"
        element={
          <ProtectedRoute requireHostelAdmin>
            <Layout>
              <Credits />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route path="/platform-settings" element={<Navigate to="/settings" replace />} />
      <Route path="/billing-tools" element={<Navigate to="/dashboard" replace />} />

      <Route
        path="/subscription-admin"
        element={
          <ProtectedRoute requireSuperAdmin>
            <Layout>
              <SubscriptionAdmin />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/bookings"
        element={
          <ProtectedRoute requireHostelAdmin blockHostelAdmin>
            <Layout>
              <Bookings />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/scheduling-rules"
        element={
          <ProtectedRoute requireHostelAdmin blockHostelAdmin>
            <Layout>
              <SchedulingRules />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route path="*" element={<Navigate to="/dashboard" />} />
    </Routes>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <WidgetVisibilityProvider>
        <Router>
          <AppRoutes />
        <InstallAppPrompt />
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
        />
        </Router>
      </WidgetVisibilityProvider>
    </AuthProvider>
  );
};

export default App;

